import { ResourceType } from "@common/types/auditLog";
import { APIProcessName } from "../process-names";
import { HistoryGroupBy } from "./history-fields";
import type { HistorySourceTypeConfig } from "./history-source-config";
import type {
    PlatformHistoryTwins,
    PlatformHistoryTwinsBySourceType,
} from "../history-platform-twins";
import {
    expandWithPlatformTwins,
    toPlatformTwinSources,
} from "../history-platform-twins";
import { ACCOUNT_FIELDS, ACCOUNT_GROUP_FIELDS } from "./source-fields";

interface CoaSource {
    sourceType: APIProcessName;
    groupBy: HistoryGroupBy.NONE | HistoryGroupBy.FIELD;
    resourceTypes: Array<ResourceType.ACCOUNT | ResourceType.ACCOUNT_GROUP>;
    platformTwins: PlatformHistoryTwins;
}

// The group internals cascade to accounts; the editor's delete and move take
// rows of either kind.
const COA_SOURCES: readonly CoaSource[] = [
    {
        sourceType: APIProcessName.COA_ACCOUNT_CREATE,
        groupBy: HistoryGroupBy.NONE,
        resourceTypes: [ResourceType.ACCOUNT],
        platformTwins: [
            APIProcessName.PLATFORM_CREATE_ACCOUNT,
            APIProcessName.PLATFORM_BULK_CREATE_ACCOUNTS,
        ],
    },
    {
        sourceType: APIProcessName.COA_CHART_EDITOR_ACCOUNT_ADD,
        groupBy: HistoryGroupBy.NONE,
        resourceTypes: [ResourceType.ACCOUNT],
        platformTwins: null,
    },
    {
        sourceType: APIProcessName.COA_ACCOUNT_DISABLE,
        groupBy: HistoryGroupBy.NONE,
        resourceTypes: [ResourceType.ACCOUNT],
        platformTwins: [APIProcessName.PLATFORM_DISABLE_ACCOUNT],
    },
    {
        sourceType: APIProcessName.COA_CHART_EDITOR_ACCOUNT_DISABLED,
        groupBy: HistoryGroupBy.NONE,
        resourceTypes: [ResourceType.ACCOUNT],
        platformTwins: null,
    },
    {
        sourceType: APIProcessName.COA_ACCOUNT_ENABLE,
        groupBy: HistoryGroupBy.NONE,
        resourceTypes: [ResourceType.ACCOUNT],
        platformTwins: [APIProcessName.PLATFORM_ENABLE_ACCOUNT],
    },
    {
        sourceType: APIProcessName.COA_CHART_EDITOR_ACCOUNT_ENABLED,
        groupBy: HistoryGroupBy.NONE,
        resourceTypes: [ResourceType.ACCOUNT],
        platformTwins: null,
    },
    {
        sourceType: APIProcessName.COA_ACCOUNT_DELETE,
        groupBy: HistoryGroupBy.NONE,
        resourceTypes: [ResourceType.ACCOUNT],
        platformTwins: [APIProcessName.PLATFORM_DELETE_ACCOUNT],
    },
    {
        sourceType: APIProcessName.COA_ACCOUNT_MERGE,
        groupBy: HistoryGroupBy.NONE,
        resourceTypes: [ResourceType.ACCOUNT],
        platformTwins: [APIProcessName.PLATFORM_MERGE_ACCOUNTS],
    },
    {
        sourceType: APIProcessName.COA_ACCOUNT_ROLE_REASSIGN,
        groupBy: HistoryGroupBy.NONE,
        resourceTypes: [ResourceType.ACCOUNT],
        platformTwins: null,
    },
    {
        sourceType: APIProcessName.ACCOUNTING_REMOVE_OPENING_BALANCE,
        groupBy: HistoryGroupBy.FIELD,
        resourceTypes: [ResourceType.ACCOUNT],
        platformTwins: [APIProcessName.PLATFORM_DELETE_OPENING_BALANCE],
    },
    {
        sourceType: APIProcessName.COA_CHART_EDITOR_DELETE,
        groupBy: HistoryGroupBy.NONE,
        resourceTypes: [ResourceType.ACCOUNT, ResourceType.ACCOUNT_GROUP],
        platformTwins: null,
    },
    {
        sourceType: APIProcessName.COA_ENTITY_ACCOUNT_GROUP_CREATE,
        groupBy: HistoryGroupBy.NONE,
        resourceTypes: [ResourceType.ACCOUNT, ResourceType.ACCOUNT_GROUP],
        platformTwins: [APIProcessName.PLATFORM_CREATE_ACCOUNT_GROUP],
    },
    {
        sourceType: APIProcessName.COA_CHART_EDITOR_GROUP_ADD,
        groupBy: HistoryGroupBy.NONE,
        resourceTypes: [ResourceType.ACCOUNT_GROUP],
        platformTwins: null,
    },
    {
        sourceType: APIProcessName.COA_ENTITY_ACCOUNT_GROUP_DELETE,
        groupBy: HistoryGroupBy.NONE,
        resourceTypes: [ResourceType.ACCOUNT, ResourceType.ACCOUNT_GROUP],
        platformTwins: [APIProcessName.PLATFORM_DELETE_ACCOUNT_GROUP],
    },
    {
        sourceType: APIProcessName.COA_ACCOUNT_UPDATE,
        groupBy: HistoryGroupBy.FIELD,
        resourceTypes: [ResourceType.ACCOUNT],
        platformTwins: [APIProcessName.PLATFORM_UPDATE_ACCOUNT],
    },
    {
        sourceType: APIProcessName.COA_CHART_EDITOR_ACCOUNT_UPDATE,
        groupBy: HistoryGroupBy.FIELD,
        resourceTypes: [ResourceType.ACCOUNT],
        platformTwins: null,
    },
    {
        sourceType: APIProcessName.COA_CHART_EDITOR_MOVE,
        groupBy: HistoryGroupBy.FIELD,
        resourceTypes: [ResourceType.ACCOUNT, ResourceType.ACCOUNT_GROUP],
        platformTwins: null,
    },
    {
        sourceType: APIProcessName.COA_ENTITY_ACCOUNT_GROUP_UPDATE,
        groupBy: HistoryGroupBy.FIELD,
        resourceTypes: [ResourceType.ACCOUNT, ResourceType.ACCOUNT_GROUP],
        platformTwins: [APIProcessName.PLATFORM_UPDATE_ACCOUNT_GROUP],
    },
    {
        sourceType: APIProcessName.COA_CHART_EDITOR_GROUP_RENAME,
        groupBy: HistoryGroupBy.FIELD,
        resourceTypes: [ResourceType.ACCOUNT_GROUP],
        platformTwins: null,
    },
];

// Exported because the query service restricts the non-grouped feed by the
// NONE set and the details endpoints by the full set.
export const COA_NONE_SOURCE_TYPES: readonly string[] = expandWithPlatformTwins(
    COA_SOURCES.filter(({ groupBy }) => groupBy === HistoryGroupBy.NONE),
);

export const COA_RESOURCE_TYPES: readonly ResourceType[] = [
    ResourceType.ACCOUNT,
    ResourceType.ACCOUNT_GROUP,
];

export const COA_ACTIVITY_SOURCE_TYPES: readonly string[] =
    expandWithPlatformTwins(COA_SOURCES);

// Sources whose UPDATE rows are cascades of another account's change: children
// lifted by a group delete, the target that receives a merged account's role.
const COA_CASCADED_UPDATE_INTERNAL_SOURCE_TYPES: readonly string[] = [
    APIProcessName.COA_ENTITY_ACCOUNT_GROUP_DELETE,
    APIProcessName.COA_CHART_EDITOR_DELETE,
    APIProcessName.COA_ACCOUNT_MERGE,
];

export const COA_CASCADED_UPDATE_SOURCE_TYPES: readonly string[] =
    expandWithPlatformTwins(
        COA_SOURCES.filter(({ sourceType }) =>
            COA_CASCADED_UPDATE_INTERNAL_SOURCE_TYPES.includes(sourceType),
        ),
    );

const COA_FIELDS_BY_RESOURCE_TYPE = {
    [ResourceType.ACCOUNT]: ACCOUNT_FIELDS,
    [ResourceType.ACCOUNT_GROUP]: ACCOUNT_GROUP_FIELDS,
};

export const accountSources: HistorySourceTypeConfig[] = COA_SOURCES.map(
    ({ sourceType, groupBy, resourceTypes, platformTwins }) => ({
        sourceType,
        resourceTypeConfigs: resourceTypes.map((resourceType) => ({
            resourceType,
            fields: COA_FIELDS_BY_RESOURCE_TYPE[resourceType],
        })),
        groupBy,
        platformTwins,
        enabled: true,
    }),
);

const COA_TEMPLATE_APPLY_PLATFORM_TWINS: PlatformHistoryTwinsBySourceType = {
    [APIProcessName.CHART_OF_ACCOUNTS_TEMPLATE_APPLY]: null,
    [APIProcessName.ENTITY_CREATE]: null,
    [APIProcessName.ORGANIZATION_CREATE_CLIENT]: null,
    [APIProcessName.CHART_OF_ACCOUNTS_TEMPLATE_APPLY_UNDO]: null,
};

export const coaTemplateApplySources: HistorySourceTypeConfig[] =
    toPlatformTwinSources(COA_TEMPLATE_APPLY_PLATFORM_TWINS).map(
        ({ sourceType, platformTwins }) => ({
            sourceType,
            resourceTypeConfigs: [
                {
                    resourceType:
                        ResourceType.chartOfAccountsTemplateApplyEvent,
                    fields: [],
                },
            ],
            groupBy: HistoryGroupBy.SOURCE_COUNT_DISTINCT,
            platformTwins,
            enabled: true,
        }),
    );

export const COA_TEMPLATE_ACTIVITY_SOURCE_TYPES = coaTemplateApplySources.map(
    (config) => config.sourceType,
);
